import React from 'react';
import { Router, Route, IndexRoute, browserHistory } from 'react-router';
import Root from './components/Root';
import PropertyGroupList from './components/PropertyGroupList';
import Listing from './components/Listing';
import SubPage from './components/SubPage';
import ListingMap from './components/ListingMap';
import ParcelMap from './components/ParcelMap';
import ListingSources from './components/ListingSources';
import TrueOwners from './components/TrueOwners';
import OwnerResearch from './components/OwnerResearch';
import PropertyMap from './components/PropertyMap';
import SellerList from './components/SellerList';
import AgentList from './components/AgentList';
import FirmList from './components/FirmList';
import TenantList from './components/TenantList';
import LenderList from './components/LenderList';
import TeamMemberList from './components/TeamMemberList';
import VendorList from './components/VendorList';
import Stats from './components/Stats';
import GeoCoder from './components/GeoCoder';
import Inventory from './components/Inventory';
import AgentPipeline from './components/AgentPipeline';
import AgentCommissionBreakdown from './components/AgentCommissionBreakdown';
import Error404 from './components/Error404';
import EscrowTracking from "./components/EscrowTracking/EscrowTracking";
import AgentPipelineDetails from './components/AgentPipelineDetails/AgentPipelineDetails';
import AgentPipelineBucket from './components/AgentPipelineBucket/AgentPipelineBucket';
import BuyerStateBreakdown from './components/BuyerStateBreakdown';
import ListingFilter from './components/ListingFilter';
import SellerListFilter from "./components/SellerListFilter";
import AgentListFilter from "./components/AgentListFilter";
import FirmListFilter from "./components/FirmListFilter";
import TenantListFilter from "./components/TenantListFilter";
import TeamMemberListFilter from "./components/TeamMemberListFilter";
import VendorListFilter from "./components/VendorListFilter";
import TransactionBreakdown from "./components/TransactionBreakdown";
import UploadCostarFile from "./components/UploadCostarFile";
import MarketTransactionBreakdown from './components/MarketTransactionBreakdown/MarketTransactionBreakdown';
import PropertyStatistics from "./components/PropertyStatistics";
import Buckets from "./components/Buckets/Buckets";
import BucketsDetails from "./components/BucketsDetails/BucketsDetails";
import SummitFees from "./components/SummitFees";
import CRUDProperty from './components/CRUDProperty';
import CRUDTenant from './components/CRUDTenant';
import CRUDLender from './components/CRUDLender';
import CRUDFirm from './components/CRUDFirms';
import CRUDAgent from './components/CRUDAgent';
import CRUDBuyer from './components/CRUDBuyer';
import CRUDSeller from './components/CRUDSeller';
import CRUDInvestor from './components/CRUDInvestor';
import InvestorList from './components/InvestorList';
import InvestorListFilter from './components/InvestorListFilter';
import HscPipeline from "./components/HscPipeline/HscPipeline";
import HscTracking from "./components/HscTracking/HscTracking";
import LenderListFilter from "./components/LenderListFilter";
import HscPipelineFees from "./components/HscPipelineFees";
import Training from "./components/Training";
import BookletManager from "./components/Training/BookletManager";
import PrintBooklet from "./components/Training/PrintBooklet";
import PrintContinuum from "./components/Training/PrintContinuum";
import TrainingSwitch from "./components/TrainingSwitch/TrainingSwitch";
import ChapterContentSwitch from "./components/ChapterContentSwitch/ChapterContentSwitch";
import AgentCommissionDetails from "./components/AgentCommissionDetails/AgentCommissionDetails";
import MarketShareReport from './components/MarketShareReport/MarketShareReport';
import HscStats from './components/HscStats/HscStats';
import PeakTargetList from './components/PeakTargetList/PeakTargetList';
import CRUDTeamMember from './components/CRUDTeamMember';
import AccessLevels from './components/AccessLevels';
import {routeGuard, guardedPage, TeamMemberPageAccess} from './components/RequireAccess/RequireAccess';
import HscFollowUp from './components/HscFollowUp/HscFollowUp';
import CRUDProposal from './components/CRUDProposal';
import CRUDVendor from './components/CRUDVendor';
import HotlistPipelineDetails from './components/HotlistPipelineDetails/HotlistPipelineDetails';
import OfficesList from './components/OfficesList/OfficesList';
import Links from './components/Links';
import MailMerge from './components/MailMerge';
import WeeklyActivity from './components/WeeklyActivity';

/*
 * Every page's guard comes from one table, frontend/src/route-access.js, keyed
 * by the path as written here; the sidebar reads the same table. A group of
 * routes under one guard shares one rule (node-backend/tests/test-route-access.js
 * checks both). Nested child paths take their parent's guard.
 */
const StatsPage = guardedPage('', Stats);

export default (
	<Router history={browserHistory}>
		{/* The Friday calls & meetings form agents open from their email.
		    Outside Root because it has no sign-in: the token in the link is
		    the agent's own and reaches only that week's numbers. */}
		<Route path={arc.path.base + 'weekly-activity/:token'} component={WeeklyActivity}/>
		<Route path={arc.path.base} component={Root}>
			<IndexRoute component={StatsPage}/>
			<Route path='links' component={Links}/>
			{/* Outside the ListingFilter group: the property set arrives from
			    the list that was already filtered, so the filter panel would
			    only offer to change something this page cannot act on. */}
			<Route component={routeGuard('mail-merge')}>
				<Route path='mail-merge' component={MailMerge}/>
			</Route>
			{/* Declared before the ListingFilter group so 'parcels' is not
			    swallowed by the map/:id route inside it. */}
			<Route component={routeGuard('map/parcels')}>
				<Route path='map/parcels' component={ParcelMap}/>
			</Route>
			{/* Company training material: open to every signed-in member
			    (Claritask 549358). */}
			<Route path='training' component={Training}/>
			<Route path='print-booklet/:slug(/:chapterSlug)' component={PrintBooklet}/>
			<Route path='print-continuum/:slug' component={PrintContinuum}/>
			<Route path='training/:slug' component={TrainingSwitch}/>
			<Route path='training/:slug/:slug2' component={ChapterContentSwitch}>
				<Route path=':slug3'>
					<Route path=':slug4'>
						<Route path=':slug5'/>
					</Route>
				</Route>
			</Route>
			<Route component={routeGuard('list')}>
				<Route component={ListingFilter}>
					<Route path='list' component={Listing}/>
					<Route path='property-groups' component={PropertyGroupList}/>
					<Route path='map' component={ListingMap}/>
					<Route path='map/:id' component={PropertyMap}/>
					<Route path='buyer-state-breakdown' component={BuyerStateBreakdown}/>
					<Route path='transaction-breakdown' component={TransactionBreakdown}/>
					<Route path='market-transaction-breakdown' component={MarketTransactionBreakdown}/>
					<Route path='market-share' component={MarketShareReport}/>
				</Route>
			</Route>
			<Route component={routeGuard('property')}>
				<Route path='property' component={CRUDProperty}/>
			</Route>
			<Route component={routeGuard('property/:id')}>
				<Route path='property/:id' component={(props) => <CRUDProperty {...props} edit/>}/>
			</Route>
			<Route component={routeGuard('property-statistics/:id')}>
				<Route path='property-statistics/:id' component={PropertyStatistics}/>
			</Route>
			<Route component={routeGuard('agent-pipeline')}>
				<Route path='agent-pipeline' component={AgentPipeline}/>
				<Route path='agent-pipeline/:id' component={(props) => <AgentPipelineDetails {...props} isAgentPipeline/>}/>
			</Route>
			<Route component={routeGuard('inventory')}>
				<Route path='inventory' component={Inventory}/>
			</Route>
			<Route component={routeGuard('escrow-tracking')}>
				<Route path='escrow-tracking' component={EscrowTracking}>
					<Route path=':id'/>
				</Route>
			</Route>
			<Route component={routeGuard('buckets')}>
				<Route path='buckets' component={Buckets}/>
				<Route path='buckets/:id' component={BucketsDetails}/>
			</Route>
			<Route component={routeGuard('agent-pipeline/bucket/:bucket')}>
				<Route path='agent-pipeline/bucket/:bucket' component={AgentPipelineBucket}/>
			</Route>
			<Route component={routeGuard('summit-fees')}>
				<Route path='summit-fees' component={SummitFees}/>
			</Route>
			<Route component={routeGuard('pipeline')}>
				<Route path='pipeline' component={HscPipeline}/>
				<Route path='pipeline/:status' component={HscPipelineFees}/>
			</Route>
			<Route component={routeGuard('training-booklet')}>
				<Route path='training-booklet' component={BookletManager}/>
			</Route>
			<Route component={routeGuard('agent-commission-breakdown')}>
				<Route path='agent-commission-breakdown' component={AgentCommissionBreakdown}/>
				<Route path='agent-commission-breakdown/paid-procured/:year/:id/procured' component={AgentCommissionDetails}/>
				<Route path='agent-commission-breakdown/paid-procured/:year/:id/paid' component={AgentCommissionDetails}/>
				<Route path='agent-commission-breakdown/:year/:id/procured' component={AgentCommissionDetails}/>
				<Route path='agent-commission-breakdown/:year/:id/paid' component={AgentCommissionDetails}/>
			</Route>
			{/* Anyone with the team list; everyone for their own record (their Forms). */}
			<Route path='teamMember/:id' component={TeamMemberPageAccess}>
				<IndexRoute component={(props) => <CRUDTeamMember {...props} edit />}/>
			</Route>
			<Route component={routeGuard('access-levels')}>
				<Route path='access-levels' component={AccessLevels}/>
			</Route>
			<Route component={routeGuard('proposals')}>
				<Route path='proposals' component={CRUDProposal}/>
			</Route>
			<Route component={routeGuard('upload-file')}>
				<Route component={SubPage}>
					<Route path='upload-file' component={UploadCostarFile}/>
				</Route>
			</Route>
			<Route component={routeGuard('listing-sources')}>
				<Route path='listing-sources' component={ListingSources}/>
			</Route>
			<Route component={routeGuard('true-owners')}>
				<Route path='true-owners' component={TrueOwners}/>
			</Route>
			<Route component={routeGuard('owner-research')}>
				<Route path='owner-research' component={OwnerResearch}/>
			</Route>
			<Route component={routeGuard('teamMembers')}>
				<Route component={TeamMemberListFilter}>
					<Route path='teamMembers' component={TeamMemberList}/>
				</Route>
			</Route>
			<Route component={routeGuard('vendors')}>
				<Route component={VendorListFilter}>
					<Route path='vendors' component={VendorList}/>
				</Route>
			</Route>
			<Route component={routeGuard('offices')}>
				<Route path='offices' component={OfficesList}/>
			</Route>
			<Route component={routeGuard('sellers')}>
				<Route component={SellerListFilter}>
					<Route path='sellers' component={SellerList}/>
				</Route>
			</Route>
			<Route component={routeGuard('agents')}>
				<Route component={AgentListFilter}>
					<Route path='agents' component={AgentList}/>
				</Route>
			</Route>
			<Route component={routeGuard('firms')}>
				<Route component={FirmListFilter}>
					<Route path='firms' component={FirmList}/>
				</Route>
			</Route>
			<Route component={routeGuard('tenants')}>
				<Route component={TenantListFilter}>
					<Route path='tenants' component={TenantList}/>
				</Route>
			</Route>
			<Route component={routeGuard('lenders')}>
				<Route component={LenderListFilter}>
					<Route path='lenders' component={LenderList}/>
				</Route>
			</Route>
			<Route component={routeGuard('tracking')}>
				<Route path='tracking' component={HscTracking}>
					<Route path='pipeline' component={HscPipeline}/>
					<Route path='pipeline/:id' component={HscPipelineFees}/>
					<Route path=':id'/>
				</Route>
			</Route>
			<Route component={routeGuard('hotlist')}>
				<Route path='hotlist' component={HscFollowUp}/>
				<Route path='hotlist/:id/:email' component={(props) => <HotlistPipelineDetails {...props} isAgentPipeline/>}/>
				<Route path='target-list' component={PeakTargetList}/>
			</Route>
			<Route component={routeGuard('stats')}>
				<Route path='stats' component={HscStats}/>
			</Route>
			<Route component={routeGuard('geocoder')}>
				<Route component={SubPage}>
					<Route path='geocoder'>
						<IndexRoute component={GeoCoder}/>
					</Route>
				</Route>
			</Route>
			<Route component={routeGuard('firm')}>
				<Route path='firm'>
					<IndexRoute component={CRUDFirm}/>
					<Route path=':id' component={(props) => <CRUDFirm {...props} edit />} />
				</Route>
			</Route>
			<Route component={routeGuard('tenant')}>
				<Route path='tenant'>
					<IndexRoute component={CRUDTenant}/>
					<Route path=':id' component={(props) => <CRUDTenant {...props} edit />} />
				</Route>
			</Route>
			<Route component={routeGuard('lender')}>
				<Route path='lender'>
					<IndexRoute component={CRUDLender}/>
					<Route path=':id' component={(props) => <CRUDLender {...props} edit />} />
				</Route>
			</Route>
			<Route component={routeGuard('agent')}>
				<Route path='agent'>
					<IndexRoute component={CRUDAgent}/>
					<Route path=':id' component={(props) => <CRUDAgent {...props} edit />} />
				</Route>
			</Route>
			<Route component={routeGuard('buyer')}>
				<Route path='buyer'>
					<IndexRoute component={CRUDBuyer}/>
					<Route path=':id' component={(props) => <CRUDBuyer {...props} edit />} />
				</Route>
			</Route>
			<Route component={routeGuard('seller')}>
				<Route path='seller'>
					<IndexRoute component={CRUDSeller}/>
					<Route path=':id' component={(props) => <CRUDSeller {...props} edit />} />
				</Route>
			</Route>
			<Route component={routeGuard('investors')}>
				<Route component={InvestorListFilter}>
					<Route path='investors' component={InvestorList}/>
				</Route>
			</Route>
			<Route component={routeGuard('investor')}>
				<Route path='investor'>
					<IndexRoute component={CRUDInvestor}/>
					<Route path=':id' component={(props) => <CRUDInvestor {...props} edit />} />
				</Route>
			</Route>
			<Route component={routeGuard('teamMember')}>
				<Route path='teamMember' component={CRUDTeamMember}/>
			</Route>
			<Route component={routeGuard('vendor')}>
				<Route path='vendor'>
					<IndexRoute component={CRUDVendor}/>
					<Route path=':id' component={(props) => <CRUDVendor {...props} edit />} />
				</Route>
			</Route>
			<Route path='*' component={Error404}/>
		</Route>
	</Router>
);
